// Tool-level rules for the Playground: can it run, and how risky is running it.
import type { BadgeTone } from '../../components/ui'
import { apiFetch } from '../../api/client'
import type { ConnectorHint, ExecuteToolResponse, ToolSchemaEntry, ToolSearchResult } from '../../api/types'

export type ToolRisk = 'read' | 'write' | 'destructive' | 'unknown'

/**
 * Derived from MCP tool annotations. Missing annotations are treated as `unknown`,
 * which — like `write` — requires confirmation: the UI fails safe.
 */
export function riskOf(tool: ToolSearchResult): ToolRisk {
  const a = tool.annotations
  if (!a || (a.readOnlyHint === undefined && a.destructiveHint === undefined)) return 'unknown'
  if (a.destructiveHint) return 'destructive'
  if (a.readOnlyHint) return 'read'
  return 'write'
}

export const RISK_BADGE: Record<ToolRisk, { tone: BadgeTone; label: string }> = {
  read: { tone: 'success', label: 'Read-only' },
  write: { tone: 'warning', label: 'Writes data' },
  destructive: { tone: 'error', label: 'Destructive' },
  unknown: { tone: 'neutral', label: 'Effect not declared' },
}

export interface RunBlock {
  title: string
  body: string
  /** Where to fix it, if anywhere in this app. */
  action?: { label: string; to: string }
}

/** Why a tool can't be executed from the Playground right now, or null if it can. */
export function runBlock(tool: ToolSearchResult): RunBlock | null {
  const toolkit = tool.server_type ?? ''
  const fix = { label: 'Open in Toolkits', to: `/toolkits?search=${encodeURIComponent(toolkit)}` }
  if (tool.policy_status === 'denied') {
    return {
      title: 'Blocked by workspace policy',
      body: `The ${toolkit || 'toolkit'} connector is denied by this workspace’s connector policy${tool.policy_reason ? ` (${tool.policy_reason})` : ''}. Ask a workspace owner or admin to allow it.`,
    }
  }
  switch (tool.connection_status) {
    case 'connected':
      return null
    case 'needs_connect':
      return {
        title: 'Connect your account first',
        body: `${toolkit || 'This toolkit'} uses OAuth and your account isn’t connected (or needs reconnecting). Connect it, then come back to run this tool.`,
        action: fix,
      }
    case 'missing_secrets':
      return {
        title: 'Toolkit not configured',
        body: `${toolkit || 'This toolkit'} is missing the secrets it needs to run in this workspace. Add them in Toolkits.`,
        action: fix,
      }
    case 'pending_config':
      return {
        title: 'Toolkit is still being configured',
        body: `${toolkit || 'This toolkit'} is being set up. Try again once it shows as connected.`,
        action: fix,
      }
    default:
      return {
        title: 'Not connected',
        body: `${toolkit || 'This toolkit'} isn’t ready (status: ${tool.connection_status}).`,
        action: fix,
      }
  }
}

export const CONNECTION_BADGE: Record<string, { tone: BadgeTone; label: string }> = {
  connected: { tone: 'success', label: 'Connected' },
  needs_connect: { tone: 'warning', label: 'Needs connect' },
  missing_secrets: { tone: 'warning', label: 'Missing secrets' },
  pending_config: { tone: 'info', label: 'Configuring' },
}

export function connectionBadge(status: string): { tone: BadgeTone; label: string } {
  return CONNECTION_BADGE[status] ?? { tone: 'neutral', label: status.replace(/_/g, ' ') }
}

// ---------------------------------------------------------------------------
// Run history (browser session only, never sent anywhere).

export type RunStatus = 'success' | 'error'

export interface RunRecord {
  id: string
  tool: ToolSearchResult
  args: Record<string, unknown>
  status: RunStatus
  /** HTTP status of the execute call (200 even when the tool itself failed). */
  httpStatus: number
  latencyMs: number
  at: string
  /** Response body (DualModeResult) or the problem-detail error. */
  body: unknown
}

export const HISTORY_LIMIT = 10
const historyKey = (ws: string | null) => `symone.pg.history.${ws ?? 'none'}`

export function readHistory(ws: string | null): RunRecord[] {
  try {
    const raw = sessionStorage.getItem(historyKey(ws))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as RunRecord[]).slice(0, HISTORY_LIMIT) : []
  } catch {
    return []
  }
}

export function writeHistory(ws: string | null, runs: RunRecord[]) {
  try {
    sessionStorage.setItem(historyKey(ws), JSON.stringify(runs.slice(0, HISTORY_LIMIT)))
  } catch {
    // Storage full or unavailable — history stays in memory for this page view.
  }
}

/** `success: false` in a 200 body is a tool-level failure, not a transport error. */
export function statusOf(body: ExecuteToolResponse): RunStatus {
  return body.success === false ? 'error' : 'success'
}

// ---------------------------------------------------------------------------
// Slim search results (catalog routing A4).

/** First sentence of a description, without a leading "[Category]" tag (≤160 chars), like SlimToolResult.summary. */
export function summaryOf(description: string | null | undefined): string {
  const d = (description ?? '').replace(/^\s*\[[^\]]+\]\s*/, '').trim()
  const m = d.match(/^.*?[.!?](?=\s|$)/)
  const first = (m ? m[0] : d).trim()
  return first.length > 160 ? first.slice(0, 159).trimEnd() + '…' : first
}

export function hintStatusLabel(status: ConnectorHint['status']): string {
  return status === 'needs_connect' ? 'Needs sign-in' : 'Not set up'
}

/**
 * Full schema for a tool picked from the slim list: GET /tools/{tool_name}/schema (the
 * REST mirror of SYMONE_GET_TOOL_SCHEMAS, same gating). A gated name rejects with the
 * gateway's problem detail: 404 `not_found` or 424 `connection_required`, each with the
 * meta-tool's per-name code in `schema_error`.
 */
export function fetchToolSchema(name: string): Promise<ToolSchemaEntry> {
  return apiFetch<ToolSchemaEntry>(`/tools/${encodeURIComponent(name)}/schema`)
}

/** A slim search result completed with its fetched schema (the schema entry's annotations and policy win). */
export function withSchema(tool: ToolSearchResult, entry: ToolSchemaEntry): ToolSearchResult {
  return {
    ...tool,
    input_schema: entry.inputSchema,
    annotations: entry.annotations ?? tool.annotations,
    ...(entry.policy_status ? { policy_status: entry.policy_status } : {}),
    ...(entry.policy_reason ? { policy_reason: entry.policy_reason } : {}),
  }
}
